import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Trash2, Check, Loader2, Users, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMessages = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('messages');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [messagesRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/admin/messages`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/newsletter/subscribers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setMessages(messagesRes.data);
      setSubscribers(subscribersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/admin/messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/admin/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const unreadCount = messages.filter(m => m.status === 'unread').length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-messages">
      <Navbar />

      <div className="pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" onClick={() => navigate('/admin')} className="-ml-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">Messages & Subscribers</h1>
              <p className="text-slate-600">Manage contact messages and newsletter subscribers</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mb-6">
            <Button 
              variant={activeTab === 'messages' ? 'default' : 'outline'}
              onClick={() => setActiveTab('messages')}
            >
              <Mail className="w-4 h-4 mr-2" />
              Messages
              {unreadCount > 0 && (
                <Badge className="ml-2 bg-red-500">{unreadCount}</Badge>
              )}
            </Button>
            <Button 
              variant={activeTab === 'subscribers' ? 'default' : 'outline'}
              onClick={() => setActiveTab('subscribers')}
            >
              <Users className="w-4 h-4 mr-2" />
              Newsletter ({subscribers.length})
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/admin/newsletter')}
              className="ml-auto"
            >
              <Send className="w-4 h-4 mr-2" />
              Compose Newsletter
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : activeTab === 'messages' ? (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading">Contact Messages</CardTitle>
              </CardHeader>
              <CardContent>
                {messages.length > 0 ? (
                  <div className="divide-y divide-slate-200">
                    {messages.map((msg) => (
                      <div key={msg.id} className={`py-4 ${msg.status === 'unread' ? 'bg-blue-50/50 -mx-6 px-6' : ''}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-slate-900">{msg.subject}</h3>
                              {msg.status === 'unread' && (
                                <Badge className="bg-blue-500">New</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mb-2">
                              From: <strong>{msg.name}</strong> ({msg.email})
                            </p>
                            <p className="text-slate-700">{msg.message}</p>
                            <p className="text-xs text-slate-400 mt-2">
                              {new Date(msg.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {msg.status === 'unread' && (
                              <Button variant="outline" size="sm" onClick={() => markAsRead(msg.id)}>
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={() => window.location.href = `mailto:${msg.email}?subject=Re: ${msg.subject}`}>
                              <Mail className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => deleteMessage(msg.id)} className="text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">No messages yet</p>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading">Newsletter Subscribers</CardTitle>
              </CardHeader>
              <CardContent>
                {subscribers.length > 0 ? (
                  <div className="divide-y divide-slate-200">
                    {subscribers.map((sub) => (
                      <div key={sub.id} className="py-3 flex items-center justify-between">
                        <span className="text-slate-900">{sub.email}</span>
                        <span className="text-sm text-slate-500">
                          {new Date(sub.subscribed_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">No subscribers yet</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;
